/**
 * Pixel fonts (03 §8): the 5×7 and 3×5 atlases exported by `src/web_export/export_bundle.py`
 * (`build_fonts`) with layout identical to v6 `text()` / `text_mask()` / `text_width()`
 * (`src/stillf1/v6_common.py`).
 *
 * v6 rules reproduced here (checked against `src/test/fixtures/text_v6.json`):
 * - text is upper-cased first (Python `str.upper`, same mapping as JS `toUpperCase`), iterated by
 *   code point; a missing glyph renders blank with the font's fallback advance
 * - width = Σ(advance + spacing) − spacing, × the integer scale; '' has width 0
 * - the drawn mask of '' is 0 px wide in 5×7 but 1 px (× scale) in 3×5, so `text()` returns
 *   `scale` for an empty 3×5 string (v6 quirk, kept for parity: it shifts 'r' anchors by `scale`)
 * - anchors: 'c' shifts by ⌊w/2⌋, 'r' by w, then x and y are rounded half-to-even (Python `round`)
 * - shadow = a keyline: the mask dilated by k = max(1, scale) in a 3×3 pattern, drawn at
 *   (x − k, y − k) under the text
 * - web addition: a glyph the font lacks but `GLYPH_FALLBACKS` knows is replaced by its ASCII
 *   stand-in before layout (© → "(C)" in 3×5), so width, mask and drawing stay consistent; v6
 *   drew those blank. Glyphs the font has are never substituted (5×7 keeps ©)
 *
 * Everything except `loadFontSet` / `PixelFont.fromAtlas` is pure and runs in node.
 */
import type { FontAtlas, FontMetrics, FontSet } from '../api.ts';

/** `3x5x`: the bundle's 3×5 font plus the web-only glyphs in `EXTRA_3X5` (see there). */
export type FontName = '5x7' | '3x5' | '3x5x';
export type Anchor = 'l' | 'c' | 'r';

export interface GlyphBox {
  x: number;
  y: number;
  w: number;
  h: number;
  advance: number;
}

/** A pixel font: metrics plus the atlas alpha as a 0/1 mask (atlas w × h, row-major). */
export class PixelFont {
  readonly name: string;
  readonly height: number;
  readonly spacing: number;
  readonly fallbackAdvance: number;
  readonly metrics: FontMetrics;
  /** 1 where the atlas has ink (alpha > 0). */
  readonly ink: Uint8Array;
  readonly atlasW: number;
  readonly atlasH: number;
  /** Optional decoded atlas for GPU-side drawing (browser). */
  readonly atlas: ImageBitmap | null;
  private readonly glyphs = new Map<number, GlyphBox>();

  constructor(metrics: FontMetrics, ink: Uint8Array, atlasW: number, atlasH: number, atlas: ImageBitmap | null = null) {
    if (ink.length !== atlasW * atlasH) throw new Error(`font ${metrics.name}: atlas mask size mismatch`);
    this.metrics = metrics;
    this.name = metrics.name;
    this.height = metrics.height;
    this.spacing = metrics.spacing;
    this.fallbackAdvance = metrics.fallback.advance;
    this.ink = ink;
    this.atlasW = atlasW;
    this.atlasH = atlasH;
    this.atlas = atlas;
    for (const ch of Object.keys(metrics.glyphs)) {
      const g = metrics.glyphs[ch];
      const cp = ch.codePointAt(0);
      if (g && cp !== undefined && ch.length === String.fromCodePoint(cp).length) this.glyphs.set(cp, g);
    }
  }

  /** Build from RGBA atlas pixels (e.g. a decoded PNG): ink = alpha > 0. */
  static fromRgba(metrics: FontMetrics, rgba: ArrayLike<number>, w: number, h: number, atlas: ImageBitmap | null = null): PixelFont {
    const ink = new Uint8Array(w * h);
    for (let i = 0; i < ink.length; i++) ink[i] = (rgba[i * 4 + 3] ?? 0) > 0 ? 1 : 0;
    return new PixelFont(metrics, ink, w, h, atlas);
  }

  /** Browser: read the atlas pixels of a loaded `FontAtlas` (ImageBitmap) once. */
  static fromAtlas(fa: FontAtlas): PixelFont {
    const { width: w, height: h } = fa.atlas;
    const c = new OffscreenCanvas(w, h);
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('font: no 2d context');
    ctx.drawImage(fa.atlas, 0, 0);
    return PixelFont.fromRgba(fa.metrics, ctx.getImageData(0, 0, w, h).data, w, h, fa.atlas);
  }

  glyph(cp: number): GlyphBox | undefined {
    return this.glyphs.get(cp);
  }

  advance(cp: number): number {
    const g = this.glyphs.get(cp);
    return g ? g.advance : this.fallbackAdvance;
  }
}

export interface PixelFonts {
  '5x7': PixelFont;
  '3x5': PixelFont;
  /** 3×5 + `EXTRA_3X5` (web only; the v6 3×5 renders these characters blank) */
  '3x5x': PixelFont;
}

/**
 * Glyphs the v6 3×5 font lacks, for larger web text that must keep its meaning (v0.3.1, the
 * Esposizione column legend and notes use "=", "~" and "*"). Drawn in the 3×5 grid ('#' = ink).
 * They live in a separate font (`3x5x`), so every v6 3×5 string (and its parity fixture) is unchanged.
 */
export const EXTRA_3X5: Readonly<Record<string, readonly string[]>> = Object.freeze({
  '=': ['...', '###', '...', '###', '...'],
  '~': ['....', '.#.#', '#.#.', '....', '....'],
  '*': ['...', '#.#', '.#.', '#.#', '...'],
  '!': ['#', '#', '#', '.', '#'],
  '?': ['##.', '..#', '.#.', '...', '.#.'],
  '%': ['#.#', '..#', '.#.', '#..', '#.#'],
});

/** `base` plus extra glyphs (rows of '#'/'.', `base.height` tall), appended below the atlas, as font `name`. */
export function extendFont(base: PixelFont, extra: Readonly<Record<string, readonly string[]>>, name: string): PixelFont {
  const h = base.height;
  const entries = Object.entries(extra).filter(([ch]) => base.glyph(ch.codePointAt(0) ?? 0) === undefined);
  const rowW = entries.reduce((w, [, rows]) => w + (rows[0]?.length ?? 0) + 1, 0);
  const W = Math.max(base.atlasW, rowW);
  const y0 = base.atlasH + 1;
  const H = y0 + h;
  const ink = new Uint8Array(W * H);
  for (let y = 0; y < base.atlasH; y++) ink.set(base.ink.subarray(y * base.atlasW, (y + 1) * base.atlasW), y * W);
  const glyphs: FontMetrics['glyphs'] = { ...base.metrics.glyphs };
  let x = 0;
  for (const [ch, rows] of entries) {
    if (rows.length !== h) throw new Error(`extendFont ${name}: glyph ${ch} is not ${String(h)} rows tall`);
    const w = rows[0]?.length ?? 0;
    rows.forEach((row, gy) => {
      for (let gx = 0; gx < w; gx++) if (row[gx] === '#') ink[(y0 + gy) * W + x + gx] = 1;
    });
    glyphs[ch] = { x, y: y0, w, h, advance: w };
    x += w + 1;
  }
  return new PixelFont({ ...base.metrics, name, atlas_size: [W, H], glyphs }, ink, W, H, null);
}

/** Browser: wrap a bundle `FontSet` (ImageBitmap atlases) as pixel fonts. */
export function pixelFonts(set: FontSet): PixelFonts {
  const f3 = PixelFont.fromAtlas(set['3x5']);
  return { '5x7': PixelFont.fromAtlas(set['5x7']), '3x5': f3, '3x5x': extendFont(f3, EXTRA_3X5, '3x5x') };
}

/** Browser: fetch metrics + atlas PNG for one font of a bundle (`baseUrl` ends without '/'). */
export async function loadFontAtlas(baseUrl: string, metricsPath: string, init?: RequestInit): Promise<FontAtlas> {
  const mres = await fetch(`${baseUrl}/${metricsPath}`, init);
  if (!mres.ok) throw new Error(`font metrics ${metricsPath}: HTTP ${String(mres.status)}`);
  const metrics = (await mres.json()) as FontMetrics;
  const ares = await fetch(`${baseUrl}/${metrics.atlas}`, init);
  if (!ares.ok) throw new Error(`font atlas ${metrics.atlas}: HTTP ${String(ares.status)}`);
  const atlas = await createImageBitmap(await ares.blob(), { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  return { metrics, atlas };
}

/** Browser: load both fonts listed in `manifest.fonts` (`{ '5x7': {metrics}, '3x5': {metrics} }`). */
export async function loadFontSet(baseUrl: string, fonts: Record<string, { metrics: string } | undefined>): Promise<FontSet> {
  const p5 = fonts['5x7'];
  const p3 = fonts['3x5'];
  if (!p5 || !p3) throw new Error('manifest.fonts: 5x7 and 3x5 required');
  const [a5, a3] = await Promise.all([loadFontAtlas(baseUrl, p5.metrics), loadFontAtlas(baseUrl, p3.metrics)]);
  return { '5x7': a5, '3x5': a3 };
}

// ───────────────────────────── pure layout ─────────────────────────────

/** ASCII stand-ins for glyphs a font lacks (applied per font, after upper-casing). */
export const GLYPH_FALLBACKS: Readonly<Record<string, string>> = Object.freeze({
  '©': '(C)', '®': '(R)', '™': 'TM', '·': '-', '•': '-', '–': '-', '—': '-', '×': 'X', '…': '...',
  '‘': "'", '’': "'", '‚': ',', '“': '"', '”': '"', '„': '"', '«': '<', '»': '>', '°': 'O',
  'À': 'A', 'Á': 'A', 'Â': 'A', 'Ä': 'A', 'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E', 'Ì': 'I', 'Í': 'I',
  'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Ö': 'O', 'Ù': 'U', 'Ú': 'U', 'Ü': 'U', 'Ç': 'C', 'Ñ': 'N', 'ß': 'SS',
});

/**
 * `str` (already upper-cased) with every glyph `font` lacks replaced by its `GLYPH_FALLBACKS`
 * stand-in (when the stand-in's glyphs exist). Returns `str` itself when nothing changes.
 */
export function fontText(font: PixelFont, str: string): string {
  let out: string | null = null;
  let i = 0;
  for (const ch of str) {
    const cp = ch.codePointAt(0) ?? 32;
    const fb = font.glyph(cp) ? undefined : GLYPH_FALLBACKS[ch];
    if (fb !== undefined && Array.from(fb).every((c) => font.glyph(c.codePointAt(0) ?? 32))) out = (out ?? str.slice(0, i)) + fb;
    else if (out !== null) out += ch;
    i += ch.length;
  }
  return out ?? str;
}

/** Python `round(x)` for floats: nearest integer, ties to even. */
export function pyRound(x: number): number {
  const f = Math.floor(x);
  const d = x - f;
  if (d > 0.5) return f + 1;
  if (d < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
}

/** Upper-case exactly like the v6 fonts (Python `str.upper` ≙ JS `toUpperCase` for this corpus). */
export function upper(str: string): string {
  return str.toUpperCase();
}

/**
 * v6 `text_width(s, font, scale)`: Σ(advance + spacing) − spacing, × scale; '' → 0.
 * `toUpperCase` may allocate; hot paths should pass upper-case text (most HUD strings are).
 */
export function textWidth(font: PixelFont, str: string, scale = 1): number {
  if (str.length === 0) return 0;
  const s = fontText(font, upper(str));
  let w = 0;
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const cp = s.codePointAt(i) ?? 32;
    if (cp > 0xffff) i++;
    w += font.advance(cp) + font.spacing;
    n++;
  }
  return n === 0 ? 0 : (w - font.spacing) * scale;
}

/** v6 `text_height(font, scale)`. */
export function textHeight(font: PixelFont, scale = 1): number {
  return font.height * scale;
}

/** Width of the drawn mask = v6 `text()` return value (3×5 '' → 1 × scale, see header). */
export function maskWidth(font: PixelFont, str: string, scale = 1): number {
  if (str.length === 0) return font.name === '3x5' ? scale : 0;
  return textWidth(font, str, scale);
}

/** Placed text box in stage px (top-left of the glyph mask, before any shadow). */
export interface TextBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * v6 `text()` placement: anchor shift by the mask width, then round half-to-even.
 * Writes into `out` (allocation-free) and returns it.
 */
export function placeText(out: TextBox, font: PixelFont, str: string, x: number, y: number, scale = 1, anchor: Anchor = 'l'): TextBox {
  const w = maskWidth(font, str, scale);
  let xx = x;
  if (anchor === 'c') xx -= Math.floor(w / 2);
  else if (anchor === 'r') xx -= w;
  out.x = pyRound(xx);
  out.y = pyRound(y);
  out.w = w;
  out.h = font.height * scale;
  return out;
}

/** Shadow (keyline) thickness in stage px for a font scale: v6 `k = max(1, scale)`. */
export function shadowPad(scale: number): number {
  return Math.max(1, scale);
}

/**
 * Glyph-run raster in stage px × `px` (device px per stage px, integer): class per pixel,
 * 0 = empty, 1 = text, 2 = shadow. With a shadow the raster is padded by `shadowPad(scale)` stage
 * px on every side and its origin is at (−pad, −pad) relative to the text box.
 */
export interface GlyphRaster {
  /** raster size in raster px */
  w: number;
  h: number;
  /** text-box origin inside the raster, raster px */
  ox: number;
  oy: number;
  data: Uint8Array;
}

/**
 * v6 `text_mask(s, font, scale·px)` (1 = ink), as a fresh Uint8Array (w × h). Pure; used by the
 * text cache on a miss and by the tests. `px` multiplies the scale (device rasterization).
 */
export function textMask(font: PixelFont, str: string, scale = 1, px = 1): { w: number; h: number; data: Uint8Array } {
  const s = scale * px;
  const w = maskWidth(font, str, s);
  const h = font.height * s;
  const data = new Uint8Array(w * h);
  if (str.length === 0) return { w, h, data };
  const up = fontText(font, upper(str));
  let cx = 0;
  for (let i = 0; i < up.length; i++) {
    const cp = up.codePointAt(i) ?? 32;
    if (cp > 0xffff) i++;
    const g = font.glyph(cp);
    if (g) {
      for (let gy = 0; gy < g.h; gy++) {
        const arow = (g.y + gy) * font.atlasW + g.x;
        for (let gx = 0; gx < g.w; gx++) {
          if (font.ink[arow + gx] !== 1) continue;
          const x0 = cx + gx * s;
          const y0 = gy * s;
          for (let yy = 0; yy < s; yy++) {
            const row = (y0 + yy) * w;
            for (let xx = 0; xx < s; xx++) data[row + x0 + xx] = 1;
          }
        }
      }
    }
    cx += (g ? g.advance : font.fallbackAdvance) * s + font.spacing * s;
  }
  return { w, h, data };
}

/** Full glyph-run raster (text + optional 3×3 keyline shadow), at `scale × px` raster px per glyph px. */
export function rasterizeRun(font: PixelFont, str: string, scale: number, shadow: boolean, px = 1): GlyphRaster {
  const m = textMask(font, str, scale, px);
  if (!shadow) return { w: m.w, h: m.h, ox: 0, oy: 0, data: m.data };
  const k = shadowPad(scale) * px;
  const W = m.w + 2 * k;
  const H = m.h + 2 * k;
  const data = new Uint8Array(W * H);
  for (let y = 0; y < m.h; y++) {
    for (let x = 0; x < m.w; x++) {
      if (m.data[y * m.w + x] !== 1) continue;
      for (let dy = 0; dy <= 2 * k; dy += k) {
        const row = (y + dy) * W + x;
        for (let dx = 0; dx <= 2 * k; dx += k) data[row + dx] = 2;
      }
    }
  }
  for (let y = 0; y < m.h; y++) {
    for (let x = 0; x < m.w; x++) if (m.data[y * m.w + x] === 1) data[(y + k) * W + x + k] = 1;
  }
  return { w: W, h: H, ox: k, oy: k, data };
}
